import { type Static, type TObject, type TSchema, Type } from "typebox";

import { Identifier } from "./schema.js";

export type HttpMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT";

/** The statuses that carry the result a caller asked for. */
export type SuccessStatus = 200 | 201 | 204;

/**
 * Everything both sides need to agree on for one route, in one object: where
 * it lives, what it accepts, and what it answers with at each status.
 *
 * The API registers the route from this and the web calls it through this,
 * so a mismatch between them is a compile error rather than a crash.
 */
export type RouteDefinition<Path extends string = string> = {
  readonly body?: TSchema;
  readonly method: HttpMethod;
  /** Relative to the versioned prefix, with `:name` for each parameter. */
  readonly path: Path;
  /**
   * Omit when every path parameter is an identifier, which is almost all of
   * them; one is built from the path. Declare it when one is not, such as a
   * driver link token.
   */
  readonly params?: TObject;
  readonly querystring?: TObject;
  readonly response: { readonly [status: number]: TSchema };
  /** OpenAPI grouping. */
  readonly tags: readonly string[];
};

/** `"/a/:b/c/:d"` to `"b" | "d"`. */
export type PathParamNames<Path extends string> =
  Path extends `${string}:${infer Name}/${infer Rest}`
    ? Name | PathParamNames<`/${Rest}`>
    : Path extends `${string}:${infer Name}`
      ? Name
      : never;

type IdentifierParams<Path extends string> = TObject<{
  [Name in PathParamNames<Path>]: typeof Identifier;
}>;

export type RouteContract<
  Definition extends RouteDefinition = RouteDefinition,
> = Omit<Definition, "params"> & {
  readonly params: Definition["params"] extends TObject
    ? Definition["params"]
    : IdentifierParams<Definition["path"]>;
};

type ParamsMatchPath<Definition extends RouteDefinition> =
  Definition["params"] extends TObject
    ? [keyof Static<Definition["params"]>] extends [
        PathParamNames<Definition["path"]>,
      ]
      ? [PathParamNames<Definition["path"]>] extends [
          keyof Static<Definition["params"]>,
        ]
        ? unknown
        : { readonly params: "declares a parameter the path does not have" }
      : { readonly params: "is missing a parameter the path has" }
    : unknown;

/**
 * Declares a route contract. The path's parameters and the params schema
 * must name the same things, checked here in the type system and again when
 * the module loads, so a typo in either cannot reach a request.
 */
export function defineRoute<const Definition extends RouteDefinition>(
  definition: Definition & ParamsMatchPath<Definition>,
): RouteContract<Definition> {
  const names = pathParamNames(definition.path);
  const params =
    definition.params ??
    Type.Object(Object.fromEntries(names.map((name) => [name, Identifier])), {
      additionalProperties: false,
    });
  const declared = Object.keys(params.properties).sort();
  if (declared.join() !== [...names].sort().join()) {
    throw new Error(
      `${definition.method} ${definition.path}: params declare ${declared.join(", ") || "none"}, the path has ${names.join(", ") || "none"}.`,
    );
  }
  return { ...definition, params } as unknown as RouteContract<Definition>;
}

export function pathParamNames(path: string): string[] {
  return [...path.matchAll(/:([A-Za-z0-9_]+)/g)].map((match) => match[1]!);
}

type SchemaAt<
  Contract extends RouteContract,
  Status,
> = Status extends keyof Contract["response"]
  ? Contract["response"][Status] extends TSchema
    ? Contract["response"][Status]
    : never
  : never;

type SuccessStatusOf<Contract extends RouteContract> = Extract<
  keyof Contract["response"],
  SuccessStatus
>;

export type ParamsOf<Contract extends RouteContract> = Static<
  Contract["params"]
>;

export type QueryOf<Contract extends RouteContract> =
  Contract["querystring"] extends TObject
    ? Static<Contract["querystring"]>
    : undefined;

export type BodyOf<Contract extends RouteContract> =
  Contract["body"] extends TSchema ? Static<Contract["body"]> : undefined;

/** The body at one status: `ResponseOf<typeof getVehicle, 200>`. */
export type ResponseOf<
  Contract extends RouteContract,
  Status extends number,
> = Status extends 204 ? undefined : Static<SchemaAt<Contract, Status>>;

/**
 * What a successful call resolves to. A route that answers 200 or 201 for
 * the same result (create-or-return) yields either; a 204 yields undefined.
 */
export type SuccessOf<Contract extends RouteContract> = {
  [Status in SuccessStatusOf<Contract>]: ResponseOf<Contract, Status>;
}[SuccessStatusOf<Contract>];

/** Every body the route can answer with, errors included. */
export type AnyResponseOf<Contract extends RouteContract> = {
  [Status in keyof Contract["response"] & number]: ResponseOf<Contract, Status>;
}[keyof Contract["response"] & number];

export type StatusOf<Contract extends RouteContract> =
  keyof Contract["response"] & number;

/**
 * The path with its parameters filled in, and the query appended. Values are
 * encoded, so an identifier cannot change which route is called.
 */
export function resolvePath<Contract extends RouteContract>(
  contract: Contract,
  params: ParamsOf<Contract>,
  query?: QueryOf<Contract>,
): string {
  const values = params as Record<string, string>;
  const path = contract.path.replace(/:([A-Za-z0-9_]+)/g, (_, name: string) => {
    const value = values[name];
    if (value === undefined || value === "") {
      throw new Error(`${contract.path} needs ${name}.`);
    }
    return encodeURIComponent(value);
  });
  const search = toQueryString(query);
  return search ? `${path}?${search}` : path;
}

function toQueryString(query: unknown): string {
  if (!query) return "";
  const pairs: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    // Repeated keys for lists, which is how the API reads `?status=a&status=b`.
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item === undefined || item === null || item === "") continue;
      pairs.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`,
      );
    }
  }
  return pairs.join("&");
}
